// GoalsBar — shows monthly goal progress for revenue, conversion59, registration, avg invoice.
// Pulls saved goals from /api/goals and current month-to-date stats from props.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";

const fm = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

// `unit` says what the *current* value represents.
// When unit==="%", the current value is itself a percentage (e.g. registration rate at 84%).
// In that case we display the ABSOLUTE value capped at 100% — never 105% — and show
// a "تجاوز الهدف" badge if current > goal. For absolute metrics (revenue / avg invoice)
// we keep the goal-achievement % which can legitimately exceed 100%.
function Bar({ label, current, goal, unit = "" }) {
  const isPercentMetric = unit === "%";
  const pct = goal > 0 ? (current / goal) * 100 : 0;
  const reached = pct >= 100;
  const onTrack = pct >= 60;
  const color = reached ? "var(--success)" : onTrack ? "var(--gold)" : "var(--warn)";
  // Visual bar fill: never exceed 100% width; goal-exceedance shown as separate green strip.
  const visualWidth = isPercentMetric
    ? Math.min(100, Number(current) || 0)         // percent metric → fill = absolute %
    : Math.min(100, pct);                          // absolute metric → fill = % of goal
  const overflowWidth = isPercentMetric
    ? 0
    : Math.min(50, Math.max(0, pct - 100) * 0.5);
  const headerText = isPercentMetric
    ? `${(Number(current) || 0).toFixed(1)}%${reached ? " ✅" : ""}`
    : `${pct.toFixed(0)}%${reached ? " ✅" : ""}`;
  const subText = isPercentMetric
    ? `${(Number(current) || 0).toFixed(1)}% / الهدف ${(Number(goal) || 0).toFixed(1)}%${reached ? " · تجاوز الهدف" : ""}`
    : `${fm(current)}${unit} / ${fm(goal)}${unit}`;
  return (
    <div data-testid={`goal-bar-${label}`} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, fontSize: 12.5, color: "var(--brown)" }}>
        <b>{label}</b>
        <span style={{ color, fontWeight: 700, fontFamily: "var(--f-n)" }}>{headerText}</span>
      </div>
      <div style={{ height: 10, background: "var(--beige)", borderRadius: 6, overflow: "hidden", position: "relative" }}>
        <div style={{ width: visualWidth + "%", height: "100%", background: color, borderRadius: 6, transition: "width .6s ease" }} />
        {overflowWidth > 0 && (
          <div style={{ position: "absolute", right: 0, top: 0, height: "100%", width: overflowWidth + "%", background: "var(--success)", opacity: 0.65 }} />
        )}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6, fontFamily: "var(--f-n)", fontVariantNumeric: "tabular-nums" }}>
        {subText}
      </div>
    </div>
  );
}

export default function GoalsBar({ monthRevenue, monthRegistration, monthAvgInvoice, monthLabel, hasInvoicePhones }) {
  const [goals, setGoals] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/goals");
        if (alive) setGoals(data);
      } catch (e) { console.warn("[GoalsBar] load failed:", e?.response?.status, e?.message); }
    })();
    return () => { alive = false; };
  }, []);

  if (!goals) return null;
  const anyGoal = (goals.revenue || 0) + (goals.registration || 0) + (goals.avg_invoice || 0);
  if (anyGoal === 0) return null; // no goals set yet

  return (
    <div className="card" style={{ marginBottom: 14, padding: "14px 16px" }} data-testid="goals-bar">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 12, gap: 8 }}>
        <h4 style={{ margin: 0, fontSize: 14, color: "var(--brown-deep)" }}>🎯 تقدّم أهداف شهر {goals.month || monthLabel}</h4>
        <span style={{ fontSize: 11.5, color: "var(--muted)" }}>عدّلها من الإعدادات → الأهداف الشهرية</span>
      </div>
      <div className="g2" style={{ gap: 10 }}>
        {goals.revenue > 0 && <Bar label="الإيراد الشهري" current={monthRevenue} goal={goals.revenue} unit=" ر" />}
        {goals.registration > 0 && (
          hasInvoicePhones === false ? (
            <div data-testid="reg-no-phones" style={{ padding: 10, background: "var(--beige)", borderRadius: 8, border: "1px dashed var(--border)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 4 }}>نسبة تسجيل العملاء</div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", lineHeight: 1.7 }}>
                ⚠ بيانات الفواتير الحالية لا تحتوي على أرقام جوال — لا يمكن حساب النسبة. ارفع <b>تقرير الفواتير التفصيلي</b> (مع عمود "رقم الجوال" أو "العميل") لتفعيل هذا الهدف.
              </div>
            </div>
          ) : (
            <Bar label="نسبة تسجيل العملاء" current={monthRegistration} goal={goals.registration} unit="%" />
          )
        )}
        {goals.avg_invoice > 0 && <Bar label="متوسط الفاتورة (من ملفات مرفوعة — لا يطبّق استبعاد الفواتير الصفرية)" current={monthAvgInvoice} goal={goals.avg_invoice} unit=" ر" />}
      </div>
    </div>
  );
}
